import { html, css } from 'lit';
import { QghBase } from './base.js';
import { isLive, fmtDateTime, statusLabel, translateCountry, translateLeague } from './utils.js';

export class QghCarousel extends QghBase {
  static properties = {
    ...QghBase.properties,
    title:       { type: String },
    theme:       { type: String },
    _atStart:    { state: true },
    _atEnd:      { state: true },
    _refreshing: { state: true },
  };

  static styles = [
    ...QghBase.styles,
    css`
      :host {
        display: block;
        font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: var(--qgh-bg);
        border-radius: var(--qgh-radius);
      }
      .widget { background: var(--qgh-bg); color: var(--qgh-color); border-radius: var(--qgh-radius); overflow: hidden; }
      .header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid var(--qgh-border); }
      .header-title { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--qgh-accent); }
      .nav { display: flex; align-items: center; gap: 4px; }
      .nav-spinner {
        width: 12px; height: 12px; border-radius: 50%;
        border: 1.5px solid var(--qgh-border);
        border-top-color: var(--qgh-accent);
        animation: spin .7s linear infinite;
        opacity: 0; transition: opacity .2s;
        flex-shrink: 0;
      }
      .nav-spinner.visible { opacity: .6; }
      @keyframes spin { to { transform: rotate(360deg); } }
      .nav button {
        background: var(--qgh-nav-bg); border: none; color: var(--qgh-color);
        width: 28px; height: 28px; border-radius: 5px; cursor: pointer;
        font-size: 16px; line-height: 1;
        display: flex; align-items: center; justify-content: center;
        transition: background .15s;
      }
      .nav button:hover:not(:disabled) { background: var(--qgh-nav-hover); }
      .nav button:disabled { opacity: .3; cursor: default; }
      .track {
        display: flex;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .track::-webkit-scrollbar { display: none; }
      .card {
        container-type: inline-size;
        flex: 0 0 220px;
        scroll-snap-align: start;
        padding: 16px 12px 14px;
        text-align: center;
        border-right: 1px solid var(--qgh-border);
      }
      .card:last-child { border-right: none; }
      .card-header { font-size: 10px; opacity: .5; letter-spacing: .04em; text-align: center; margin-bottom: 4px; }
      .card-header--live { color: var(--qgh-accent); opacity: 1; display: flex; align-items: center; justify-content: center; gap: 4px; }
      .card-league { font-size: 9px; opacity: .3; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: center; }
      .matchup { display: flex; align-items: center; gap: 8px; }
      .team-side { display: flex; flex-direction: column; align-items: center; gap: 5px; flex: 1; min-width: 0; }
      .logo { width: 30px; height: 30px; object-fit: contain; }
      .logo-fb {
        width: 30px; height: 30px; border-radius: 50%;
        background: var(--qgh-fb-bg);
        display: flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 600; color: #aaa;
      }
      .team-name { font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
      .score-area { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
      .score-num { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; min-width: 22px; text-align: center; line-height: 1; }
      .score-num--live { color: var(--qgh-accent); }
      .vs { font-size: 11px; opacity: .35; font-weight: 600; }
      .dot { background: var(--qgh-accent); }
      .empty { padding: 28px; text-align: center; font-size: 13px; opacity: .35; }
      .state-box {
        padding: 32px 16px; text-align: center;
        display: flex; flex-direction: column; align-items: center; gap: 12px;
      }
      .state-logo { height: 22px; width: auto; filter: var(--qgh-logo-filter); opacity: .45; }
      .state-msg { font-size: 12px; opacity: .45; }
      .skeleton-cards { display: flex; }
      .skeleton-card { padding: 16px 12px 14px; flex: 0 0 220px; display: flex; flex-direction: column; align-items: center; gap: 10px; border-right: 1px solid var(--qgh-border); }
      .skeleton-card:last-child { border-right: none; }
      .sk { border-radius: 4px; background: var(--qgh-border); animation: shimmer 1.4s ease-in-out infinite; }
      .sk-circle { width: 30px; height: 30px; border-radius: 50%; }
      @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
    `,
  ];

  constructor() {
    super();
    this._atStart = true;
    this._atEnd = false;
    this._hasAutoScrolled = false;
    this._refreshing = false;
  }

  async _load() {
    // En la primera carga usa el skeleton del padre; en refreshes muestra spinner discreto
    if (this._hasAutoScrolled) this._refreshing = true;
    try {
      await super._load();
    } finally {
      this._refreshing = false;
    }
  }

  updated(changed) {
    if (changed.has('_fixtures') || changed.has('_loading')) {
      this.updateComplete.then(() => {
        const track = this.shadowRoot?.querySelector('.track');
        if (!track) return;
        if (!track._qghScrollBound) {
          track.addEventListener('scroll', () => this._updateNav(track), { passive: true });
          track._qghScrollBound = true;
        }
        // Auto-scroll solo en la primera carga, no en cada refresco
        if (!this._hasAutoScrolled && this._fixtures.length > 0) {
          this._hasAutoScrolled = true;
          const cards = [...track.querySelectorAll('.card')];
          // Buscar el primer live/NS — queremos mostrar el último FT justo antes
          const firstNonFTIdx = this._fixtures.findIndex(f => {
            const s = f.fixture.status.short;
            return isLive(s) || s === 'NS' || s === 'TBD';
          });
          // targetIdx: último finalizado (justo antes del primer live/NS), o último si no hay live/NS
          const targetIdx = firstNonFTIdx > 0
            ? firstNonFTIdx - 1
            : firstNonFTIdx === -1
              ? this._fixtures.length - 1
              : 0;
          if (targetIdx > 0 && cards[targetIdx]) {
            const cardRect  = cards[targetIdx].getBoundingClientRect();
            const trackRect = track.getBoundingClientRect();
            const offset    = cardRect.left - trackRect.left;
            if (offset > 0) track.scrollLeft += offset;
          }
        }
        this._updateNav(track);
      });
    }
  }

  _updateNav(track) {
    this._atStart = track.scrollLeft <= 2;
    this._atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  _scroll(dir) {
    const track = this.shadowRoot?.querySelector('.track');
    if (!track) return;
    const card = track.querySelector('.card');
    const w = card?.offsetWidth ?? 180;
    track.scrollBy({ left: dir * w, behavior: 'smooth' });
  }

  render() {
    if (this._loading) return html`
      <div class="widget">
        ${this.title ? html`<div class="header"><span class="header-title">${this.title}</span></div>` : ''}
        <div class="skeleton-cards">
          ${[...Array(3)].map(() => html`
            <div class="skeleton-card">
              <div class="sk sk-circle"></div>
              <div class="sk" style="width:70%;height:8px"></div>
              <div class="sk" style="width:50%;height:20px"></div>
              <div class="sk" style="width:40%;height:8px"></div>
            </div>`)}
        </div>
        ${this._poweredBy()}
      </div>`;

    if (this._error) return html`
      <div class="widget">
        ${this.title ? html`<div class="header"><span class="header-title">${this.title}</span></div>` : ''}
        <div class="state-box">
          <img class="state-logo" src="https://quienganahoy.com/svg/logo_qgh.svg" alt="QuienGanaHoy">
          <span class="state-msg">No se pudo cargar</span>
        </div>
        ${this._poweredBy()}
      </div>`;

    const showNav = this._fixtures.length > 1;

    return html`
      <div class="widget">
        ${this.title || showNav ? html`
          <div class="header">
            <span class="header-title">${this.title ?? ''}</span>
            <nav class="nav" aria-label="Partidos">
              <div class="nav-spinner ${this._refreshing ? 'visible' : ''}"></div>
              ${showNav ? html`
                <button @click=${() => this._scroll(-1)} ?disabled=${this._atStart}>‹</button>
                <button @click=${() => this._scroll(1)}  ?disabled=${this._atEnd}>›</button>` : ''}
            </nav>
          </div>` : ''}
        ${this._fixtures.length
          ? html`<div class="track">${this._fixtures.map(f => this._card(f))}</div>`
          : html`<div class="empty">Sin partidos disponibles</div>`}
        ${this._poweredBy()}
      </div>`;
  }

  _card(f) {
    const live  = isLive(f.fixture.status.short);
    const isNS  = f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD';
    const label = statusLabel(f);
    const g     = f.goals;
    const hasScore = !isNS && g.home !== null && g.home !== undefined;

    const logo = (team) => {
      if (!team.logo) return html`<span class="logo-fb">${team.name.charAt(0)}</span>`;
      const onError = (e) => {
        const span = document.createElement('span');
        span.className = 'logo-fb';
        span.textContent = team.name.charAt(0);
        e.target.replaceWith(span);
      };
      return html`<img class="logo" src="${team.logo}" alt="" loading="lazy" @error="${onError}">`;
    };

    const header = isNS
      ? html`<div class="card-header">${fmtDateTime(f.fixture.date)}</div>`
      : html`<div class="card-header ${live ? 'card-header--live' : ''}">
          ${live ? html`<span class="dot"></span>` : ''}${label}
        </div>`;

    return html`
      <div class="card">
        ${header}
        ${f.league?.country ? html`<div class="card-league">${translateCountry(f.league.country)} — ${translateLeague(f.league.name)}</div>` : ''}
        <div class="matchup">
          <div class="team-side">
            ${logo(f.teams.home)}
            <span class="team-name">${translateCountry(f.teams.home.name)}</span>
          </div>
          <div class="score-area">
            ${hasScore ? html`<span class="score-num ${live ? 'score-num--live' : ''}">${g.home}</span>` : ''}
            <span class="vs">vs</span>
            ${hasScore ? html`<span class="score-num ${live ? 'score-num--live' : ''}">${g.away}</span>` : ''}
          </div>
          <div class="team-side">
            ${logo(f.teams.away)}
            <span class="team-name">${translateCountry(f.teams.away.name)}</span>
          </div>
        </div>
      </div>`;
  }
}
